import React, { useContext } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { Heading2 } from '../../layout'
import { Icon } from '../../icons/Icon'
import { WorkShiftEvent, FormattedEvent, EventTypeOption } from '../../../containers/Shifts/types'
import { messages } from '../../../containers/Shifts/messages'
import { format } from 'date-fns'
import { Time } from '../../../common/types'
import { LocaleContext } from '../../../util/locale'

export const TableHeader: React.FunctionComponent = () => {
  const { formatMessage } = useIntl()
  return (
    <TableRowSection type="header">
      <Th>{formatMessage(messages.tableEventColumn)}</Th>
      <Th>{formatMessage(messages.tableDateColumn)}</Th>
      <Th>{formatMessage(messages.tableTimeColumn)}</Th>
      <Th>{formatMessage(messages.tableInfoColumn)}</Th>
    </TableRowSection>
  )
}

interface TableRowProps {
  event: WorkShiftEvent
  isMobile?: boolean
}

export const TableRow: React.FunctionComponent<TableRowProps> = ({ event }) => {
  const { locale } = useContext(LocaleContext)
  const { formatMessage } = useIntl()
  const formattedEvent = toFormattedEvent(event)
  return (
    <TableRowSection type="row">
      <Td>{formatEventType(formattedEvent.type, formatMessage)}</Td>
      <Td>{formattedEvent.date.toLocaleDateString(locale)}</Td>
      <Td>{formattedEvent.time}</Td>
      <Td>{formattedEvent.info}</Td>
    </TableRowSection>
  )
}

function toFormattedEvent(event: WorkShiftEvent): FormattedEvent {
  return {
    id: event.id,
    type: event.type,
    date: new Date(event.timestamp),
    time: (format(new Date(event.timestamp), 'HH:mm') as unknown) as Time,
    info: event.info ? event.info : '',
  }
}

function formatEventType(type: EventTypeOption, formatMessage: IntlShape['formatMessage']): string {
  switch (type) {
    case EventTypeOption.work_start:
      return formatMessage(messages.eventTypeWorkStart)
    case EventTypeOption.work_end:
      return formatMessage(messages.eventTypeWorkEnd)
    case EventTypeOption.break_start:
      return formatMessage(messages.eventTypeBreakStart)
    case EventTypeOption.break_end:
      return formatMessage(messages.eventTypeBreakEnd)
    case EventTypeOption.standby_start:
      return formatMessage(messages.eventTypeStandbyStart)
    case EventTypeOption.standby_end:
      return formatMessage(messages.eventTypeStandbyEnd)
    case EventTypeOption.wait_start:
      return formatMessage(messages.eventTypeWaitStart)
    case EventTypeOption.wait_end:
      return formatMessage(messages.eventTypeWaitEnd)
    case EventTypeOption.standby_work_start:
      return formatMessage(messages.eventTypeStandbyWorkStart)
    case EventTypeOption.standby_work_end:
      return formatMessage(messages.eventTypeStandbyWorkEnd)
    default:
      return ''
  }
}

export const Table = styled.table`
  width: 100%;
  border-spacing: 0;
`

interface TableRowSectionProps {
  padding?: string
  type?: 'header' | 'row'
}

export const TableRowSection = styled.tr<TableRowSectionProps>`
  padding: ${props => (props.padding ? props.padding : '0.5rem 0')};
  ${props =>
    props.type === 'header' &&
    css`
      border-radius: ${props => props.theme.radius.small};
      border-color: ${props => props.theme.colors.grey100};
      background-color: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.grey100)};
    `}
  ${props =>
    props.type === 'row' &&
    css`
      border-bottom: 0.5px solid;
      border-color: ${props => props.theme.colors.grey300};
    `}
`

const Th = styled.th`
  text-align: start;
  padding: 0.5rem;
`

const Td = styled.td`
  padding: 0.5rem;
  border-bottom: 0.5px solid;
  border-color: ${props => props.theme.colors.grey300};
  color: ${props => props.theme.colors.black};
  vertical-align: top;
`

interface ShiftHeaderProps {
  color?: string
}

export const ShiftHeader = styled.div<ShiftHeaderProps>`
  background-color: ${props => (props.color ? props.theme.colors[props.color] : props.theme.colors.grey300)};
  padding: 3rem 1rem 1rem 1rem;
  text-align: center;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    padding: 3.5rem 1rem 1rem 1rem;
  }
`

interface ShiftHeadingProps {
  vehicleName?: string
  vehicleRegNo?: string
  label: string
}

export const ShiftHeading: React.FunctionComponent<ShiftHeadingProps> = ({ label, vehicleName, vehicleRegNo }) => {
  return (
    <HeadingContainer>
      <Heading2 color="black">{label}</Heading2>
      {vehicleName && vehicleRegNo && (
        <Heading2 color="black">
          {vehicleName} ({vehicleRegNo.toUpperCase()}){' '}
          <HeadingIcon icon="edit" inline={true} size="shift-heading" color="black" />
        </Heading2>
      )}
    </HeadingContainer>
  )
}

const HeadingContainer = styled.div`
  margin-bottom: 1rem;
`

const HeadingIcon = styled(Icon as any)`
  vertical-align: middle;
`
